import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Anchor, Map, Sun } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.8
  }
};
export function Home() {
  const [communityInterests, setCommunityInterests] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const handleInterestToggle = (interest: string) => {
    setCommunityInterests(prev => prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]);
  };
  const handleCommunitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && communityInterests.length > 0) {
      setSubmitted(true);
      // In a real app, this would send to a backend
    }
  };
  return <div className="w-full" data-id="element-215">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-216">
        <div className="absolute inset-0 z-0" data-id="element-217">
          <img src="https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80" alt="Cabo San Lucas Ocean View" className="w-full h-full object-cover" data-id="element-218" />
          <div className="absolute inset-0 bg-navy/40 mix-blend-multiply" data-id="element-219" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-220">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="text-5xl md:text-7xl font-serif text-white mb-6 leading-tight" data-id="element-221">
            Your Cabo Life <br data-id="element-222" />
            <span className="text-gold italic" data-id="element-223">Starts Here</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="text-lg md:text-xl text-sand/90 font-light mb-10 max-w-2xl mx-auto" data-id="element-224">
            More than just purchasing property. We curate the ultimate resort
            destination lifestyle, connecting you with the community, culture,
            and vetted professionals of Los Cabos.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-225">
            <Link to="/buyers" className="bg-gold text-white px-8 py-4 uppercase tracking-wider text-sm font-medium hover:bg-gold-dark transition-colors" data-id="element-226">
              Explore Properties
            </Link>
            <Link to="/collective" className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 uppercase tracking-wider text-sm font-medium hover:bg-white/20 transition-colors" data-id="element-227">
              Meet The Collective
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-sand px-4" data-id="element-228">
        <div className="max-w-4xl mx-auto text-center" data-id="element-229">
          <motion.div {...fadeIn} data-id="element-230">
            <Anchor className="mx-auto text-gold mb-6" size={32} data-id="element-231" />
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 leading-tight" data-id="element-232">
              We believe real estate is just the canvas. <br data-id="element-233" />
              <span className="italic text-gold" data-id="element-234">
                The lifestyle is the masterpiece.
              </span>
            </h2>
            <p className="text-lg text-navy/70 leading-relaxed font-light" data-id="element-235">
              Cabo Collection is a lifestyle-first real estate experience. We
              don't just hand you the keys to a house; we hand you the keys to
              the community. From hidden beaches and exclusive golf clubs to the
              most reliable contractors and legal advisors, we are your trusted
              guides to living beautifully in Baja.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Collective Preview */}
      <section className="py-24 bg-white" data-id="element-236">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-237">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-238">
            <motion.div {...fadeIn} className="order-2 lg:order-1" data-id="element-239">
              <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80" alt="Luxury Interior" className="w-full h-[600px] object-cover rounded-sm shadow-2xl" data-id="element-240" />
            </motion.div>
            <motion.div {...fadeIn} className="order-1 lg:order-2" data-id="element-241">
              <h4 className="text-gold font-semibold tracking-widest uppercase text-sm mb-4" data-id="element-242">
                Exclusive Network
              </h4>
              <h2 className="text-4xl font-serif text-navy mb-6" data-id="element-243">
                The Collective
              </h2>
              <p className="text-navy/70 mb-8 leading-relaxed font-light text-lg" data-id="element-244">
                Building, remodeling, or simply settling into a new country
                shouldn't be stressful. The Collective is our curated directory
                of vetted, elite professionals—from architects and interior
                designers to legal firms and residency consultants.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-245">
                {['Rigorously vetted through background checks & client testimonials', 'Exclusive pricing and fast-tracked services for our clients', 'Trusted local experts who deliver on their promises'].map((item, i) => <li key={i} className="flex items-start" data-id="element-246">
                    <CheckCircle2 className="text-gold mr-3 shrink-0 mt-1" size={20} data-id="element-247" />
                    <span className="text-navy/80" data-id="element-248">{item}</span>
                  </li>)}
              </ul>
              <Link to="/collective" className="inline-flex items-center text-navy font-semibold uppercase tracking-wider hover:text-gold transition-colors group" data-id="element-249">
                Explore The Directory
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} data-id="element-250" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifestyle Grid */}
      <section className="py-24 bg-navy text-sand" data-id="element-251">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-252">
          <motion.div {...fadeIn} className="text-center mb-16" data-id="element-253">
            <h2 className="text-4xl md:text-5xl font-serif mb-4 text-white" data-id="element-254">
              The Cabo Lifestyle
            </h2>
            <p className="text-sand/70 max-w-2xl mx-auto font-light text-lg" data-id="element-255">
              Discover the unique rhythms of life in Los Cabos. From world-class
              fairways to hidden coastal gems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-256">
            {[{
            title: 'Golf Communities',
            img: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?auto=format&fit=crop&q=80',
            desc: 'Exclusive access to championship courses.'
          }, {
            title: 'Hidden Beaches',
            img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80',
            desc: 'Secret coves and swimmable waters.'
          }, {
            title: 'Culinary Scene',
            img: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&q=80',
            desc: 'Farm-to-table dining and local flavors.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.2
          }} className="group relative h-96 overflow-hidden cursor-pointer" data-id="element-257">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-258" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" data-id="element-259" />
                <div className="absolute bottom-0 left-0 p-8" data-id="element-260">
                  <h3 className="text-2xl font-serif text-white mb-2" data-id="element-261">
                    {item.title}
                  </h3>
                  <p className="text-sand/80 font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0" data-id="element-262">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Niche Community Form */}
      <section className="py-24 bg-sand" data-id="element-263">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-264">
          <motion.div {...fadeIn} data-id="element-265">
            <Sun className="mx-auto text-gold mb-6" size={32} data-id="element-266" />
            <h2 className="text-3xl md:text-4xl font-serif text-navy mb-6" data-id="element-267">
              Find Your Tribe
            </h2>
            <p className="text-navy/70 mb-10 font-light text-lg" data-id="element-268">
              We are actively developing and sourcing properties tailored to
              specific passions. Tell us what kind of community you'd love to be
              part of, and we'll keep you updated.
            </p>

            {submitted ? <div className="bg-white p-8 border border-gold/30 rounded-sm" data-id="element-269">
                <CheckCircle2 className="mx-auto text-gold mb-4" size={48} data-id="element-270" />
                <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-271">
                  Thank You
                </h3>
                <p className="text-navy/70" data-id="element-272">
                  We've noted your interests and will be in touch when the
                  perfect community emerges.
                </p>
              </div> : <form onSubmit={handleCommunitySubmit} className="bg-white p-8 md:p-12 shadow-sm text-left" data-id="element-273">
                <div className="mb-8" data-id="element-274">
                  <label className="block text-sm font-semibold text-navy uppercase tracking-wider mb-4" data-id="element-275">
                    I'm interested in communities centered around:
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-276">
                    {['Pickleball', 'Golf', 'Art & Design', 'Wellness/Yoga', 'Equestrian', 'LGBTQ+', 'Writing/Creative', 'Surfing', 'Culinary/Wine'].map(interest => <label key={interest} className="flex items-center space-x-3 cursor-pointer group" data-id="element-277">
                        <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${communityInterests.includes(interest) ? 'bg-gold border-gold' : 'border-navy/30 group-hover:border-gold'}`} data-id="element-278">
                          {communityInterests.includes(interest) && <CheckCircle2 size={14} className="text-white" data-id="element-279" />}
                        </div>
                        <span className="text-navy/80 font-light" data-id="element-280">
                          {interest}
                        </span>
                      </label>)}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4" data-id="element-281">
                  <input type="email" required placeholder="Your Email Address" value={email} onChange={e => setEmail(e.target.value)} className="flex-grow px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-282" />
                  <button type="submit" className="bg-navy text-white px-8 py-3 uppercase tracking-wider text-sm font-medium hover:bg-navy-light transition-colors whitespace-nowrap" data-id="element-283">
                    Keep Me Updated
                  </button>
                </div>
                <p className="text-xs text-navy/50 mt-4 font-light" data-id="element-284">
                  We respect your privacy. No spam, just highly curated updates
                  based on your interests.
                </p>
              </form>}
          </motion.div>
        </div>
      </section>
    </div>;
}